import classNames from 'classnames';

import ChevronLeftIcon from '@heroicons/react/20/solid/ChevronLeftIcon';
import XMarkIcon from '@heroicons/react/20/solid/XMarkIcon';
import { TeirContent } from './TierContent';
import { usePurchase } from '../../context/purchase/usePurchase';
import { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, m } from 'framer-motion';
import { PurchaseContainer } from './purchase/PurchaseContainer';
import { BILLING_FREQUENCIES, Frequency, SubscriptionContainerAnchor, Tier } from './subscriptions.constants';
import { scrollToElement } from '../../utils';
import { refreshAuthSession, subscriptionApi } from '../../api/service';
import { handleApiError } from '../../api/utils';
import { WebSocketService } from '../../api/WebSocketService';
import { SubscriptionEvent } from 'referral-types';
import toast from 'react-hot-toast';
import { ThemePrimary } from '../../context/theme/ThemeConstantsUpdated';
import { useAuth } from '../../context/auth/useAuth';

const { primaryBackground, primaryBackgroundHover, primaryText, primaryTextHover } = ThemePrimary;

const SCROLL_CONTAINER_ID = 'slideout-scroll-container';

export const SubscriptionsContainer = () => {
  const { toggleSubscribing, setCompletedSubscription } = usePurchase();
  const { setCurrentUser } = useAuth();
  const [purchaseTier, setPurchaseTier] = useState<Tier>();
  const [isWebhookConnected, setIsWebhookConnected] = useState(false);
  const [purchaseFrequency, setPurchaseFrequency] = useState<Frequency>(BILLING_FREQUENCIES[0]);
  const [clientSecrets, setClientSecrets] = useState<Array<{ priceId: string; clientSecret: string }>>([]);

  const proceed = useCallback(
    (tier: Tier, freq: Frequency) => {
      const priceId = tier.priceId[freq.value];

      setPurchaseTier(tier);
      setPurchaseFrequency(freq);
      setTimeout(() => scrollToElement(SubscriptionContainerAnchor, { scrollContainerId: SCROLL_CONTAINER_ID }), 600);

      if (clientSecrets.find((o) => o.priceId === priceId) == null) {
        subscriptionApi
          .getIntent(priceId)
          .then((r) => setClientSecrets([...clientSecrets, { priceId, clientSecret: r }]))
          .catch((e) => {
            setPurchaseTier(undefined);
            handleApiError(e);
          });
      }
    },
    [clientSecrets]
  );

  const updateFrequency = useCallback(
    (freq: Frequency) => {
      const priceId = purchaseTier?.priceId[freq.value];

      if (priceId && clientSecrets.find((o) => o.priceId === priceId) == null) {
        subscriptionApi
          .getIntent(priceId)
          .then((r) => setClientSecrets([...clientSecrets, { priceId, clientSecret: r }]))
          .catch((e) => {
            setPurchaseTier(undefined);
            handleApiError(e);
          });
      }

      setPurchaseFrequency(freq);
    },
    [clientSecrets, purchaseTier?.priceId]
  );

  const onCancel = useCallback(() => {
    setPurchaseTier(undefined);
    setPurchaseFrequency(BILLING_FREQUENCIES[0]);
    setTimeout(() => scrollToElement(SubscriptionContainerAnchor, { scrollContainerId: SCROLL_CONTAINER_ID }), 600);
  }, []);

  const onNavigateBack = useCallback(() => {
    const priceId = purchaseTier?.priceId[purchaseFrequency.value];

    setClientSecrets(clientSecrets.filter((o) => o.priceId != priceId));
    setPurchaseTier(undefined);
  }, [clientSecrets, purchaseFrequency.value, purchaseTier?.priceId]);

  const onConfirm = useCallback(async () => {
    setPurchaseTier(undefined);
    setPurchaseFrequency(BILLING_FREQUENCIES[0]);

    try {
      const refreshedUser = await refreshAuthSession();
      setCurrentUser(refreshedUser);
    } catch {
      toast.error('Token Update Failure... Please login again to apply purchase. Sorry!');
    }
  }, [setCurrentUser]);

  useEffect(() => {
    const ws = WebSocketService.getInstance();
    const handleConnectionChange = (status: boolean) => setIsWebhookConnected(status);

    const handleMessage = (data: unknown) => {
      try {
        if ((data as { type: string }).type === 'subscription:completed') {
          setCompletedSubscription((data as SubscriptionEvent).subscription);
        } else if ((data as { type: string }).type === 'subscription:error') {
          toast.error((data as ErrorEvent).message);
        }
      } catch (err) {
        console.error('Failed to parse message from server:', err);
      }
    };

    ws.subscribeConnectionStatus(handleConnectionChange);
    ws.subscribe(handleMessage);

    return () => {
      ws.unsubscribeConnectionStatus(handleConnectionChange);
      ws.unsubscribe(handleMessage);
    };
  }, [setCompletedSubscription, setIsWebhookConnected]);

  return (
    <div className={`background-circle-gradient-blue h-full w-full`}>
      <div id={SCROLL_CONTAINER_ID} className="h-full w-full overflow-x-hidden overflow-y-scroll">
        <div id={SubscriptionContainerAnchor} />
        <div className="fixed right-6 top-6 z-10 flex items-center justify-center sm:right-8 sm:top-8 lg:right-16 lg:top-12">
          {purchaseTier != null && (
            <button
              onClick={() => onNavigateBack()}
              className={classNames(primaryText, primaryTextHover, 'rounded-full p-1')}
            >
              <ChevronLeftIcon className="h-8 w-8 sm:h-12 sm:w-12" />
            </button>
          )}
          <button
            onClick={() => toggleSubscribing()}
            className={classNames(primaryBackground, primaryBackgroundHover, 'rounded-full p-1')}
          >
            <XMarkIcon className="h-8 w-8 text-white sm:h-12 sm:w-12" />
          </button>
        </div>
        <div className="relative w-full">
          <div className="mx-auto max-w-6xl">
            <AnimatePresence key="subscription_info_page">
              {purchaseTier == null && (
                <m.div
                  key="motion_subscription_info_page"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1, transition: { duration: 0.9, delay: 0.8 } }}
                  exit={{ opacity: 0 }}
                  className="absolute left-1/2 top-0 w-full -translate-x-1/2 py-20"
                >
                  <TeirContent proceed={proceed} />
                </m.div>
              )}
            </AnimatePresence>
            <AnimatePresence key="subscription_purchase_page">
              {purchaseTier && (
                <m.div
                  key="motion_subscription_page"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1, transition: { duration: 0.75, delay: 0.5 } }}
                  exit={{ opacity: 0 }}
                  className="absolute left-1/2 top-0 w-full -translate-x-1/2 py-20"
                >
                  <PurchaseContainer
                    tier={purchaseTier}
                    frequency={purchaseFrequency}
                    updateFrequency={updateFrequency}
                    onCancel={onCancel}
                    onConfirm={onConfirm}
                    isDisabled={!isWebhookConnected}
                    clientSecret={
                      clientSecrets.find((o) => o.priceId === purchaseTier.priceId[purchaseFrequency.value])
                        ?.clientSecret
                    }
                  />
                </m.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};
